import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMissatgeError } from '@/api/errors';
import {
  usePoliticaFitxers,
  usePujaDocument,
  type AmbitDocument,
  type DocumentAdminDto,
} from '@/api/administracio';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { Camp, Modal } from '@/pages/operacio/comu';
import { opcionsServei, useEstructura } from './marc/estructura';
import { midaLlegible } from './utils';
import './sistema.css';

const AMBITS: AmbitDocument[] = ['SERVEI', 'AREA', 'DEPARTAMENT'];

function Comprovacio({ ok, text }: { ok: boolean | null; text: string }) {
  return (
    <span className={`sis-check${ok == null ? '' : ok ? ' is-ok' : ' is-ko'}`}>
      <Icona nom={ok == null ? 'shield' : ok ? 'checkmark-circle' : 'xmark-circle'} />
      {text}
    </span>
  );
}

/**
 * Pujada d'un document nou o d'una versió nova (W-34). Per a un document nou primer es tria per
 * a qui és (servei i qui el pot consultar), després el fitxer i les dades.
 */
export function W34PujaDocument({
  document,
  serveiId,
  onTanca,
  onCreat,
}: {
  document?: DocumentAdminDto | null;
  serveiId?: number | null;
  onTanca: () => void;
  /** Es crida amb el document creat o actualitzat. */
  onCreat?: (d: DocumentAdminDto) => void;
}) {
  const { t } = useTranslation('administracio');
  const missatgeError = useMissatgeError();
  const estructura = useEstructura();
  const politica = usePoliticaFitxers();
  const puja = usePujaDocument();
  const [fitxer, setFitxer] = useState<File | null>(null);
  const [servei, setServei] = useState<number | ''>(document?.serveiId ?? serveiId ?? '');
  const [titol, setTitol] = useState('');
  const [categoria, setCategoria] = useState('');
  const [ambit, setAmbit] = useState<AmbitDocument>('SERVEI');
  const [comentari, setComentari] = useState('');

  const ext = fitxer?.name.split('.').pop()?.toLowerCase() ?? '';
  const p = politica.data?.data;
  const formatInvalid = !!fitxer && !!p && p.formats.length > 0 && !p.formats.includes(ext);
  const massaGran = !!fitxer && !!p && fitxer.size > p.midaMaxMb * 1024 * 1024;
  const valid = !!fitxer && servei !== '' && !formatInvalid && !massaGran;
  const resultat = puja.data;
  const versio = resultat?.darreraVersio ?? resultat?.versioVigent;
  const s = servei === '' ? undefined : estructura.servei(servei);
  const area = estructura.area(s?.areaId);
  const departament = estructura.departament(area?.departamentId);
  const nomAmbit = (a: AmbitDocument) =>
    a === 'SERVEI' ? s?.nom : a === 'AREA' ? area?.nom : departament?.nom;

  const envia = () => {
    if (!fitxer || servei === '') return;
    puja.mutate(
      {
        serveiId: servei,
        documentId: document?.id,
        fitxer,
        titol: titol.trim() || undefined,
        categoria: categoria.trim() || undefined,
        ambit,
        comentari: comentari.trim() || undefined,
      },
      { onSuccess: (d) => onCreat?.(d) },
    );
  };

  const zonaFitxer = (
    <div className="stack" style={{ gap: 10 }}>
      <label className="zona-fitxer">
        <Icona nom="paperclip" />
        <div className="zona-fitxer__titol">{t('documents.arrossega')}</div>
        {p ? (
          <div className="t-caption dim mt-4">
            {t('documents.politicaResum', {
              formats: p.formats.map((x) => x.toUpperCase()).join(', '),
              mida: p.midaMaxMb,
              max: p.maxDocuments,
            })}
          </div>
        ) : null}
        <input
          type="file"
          aria-label={t('documents.fitxer')}
          accept={p?.formats.map((x) => `.${x}`).join(',')}
          onChange={(e) => setFitxer(e.target.files?.[0] ?? null)}
        />
      </label>
      {fitxer ? (
        <div className="fitxer-fila">
          <Icona nom="doc-text" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="truncate">{fitxer.name}</div>
            <div className="sis-checks">
              <Comprovacio
                ok={p ? !formatInvalid : null}
                text={`${t('documents.checkFormat')} · ${ext.toUpperCase()}`}
              />
              <Comprovacio
                ok={p ? !massaGran : null}
                text={
                  p
                    ? t('documents.midaDe', { mida: midaLlegible(fitxer.size), max: p.midaMaxMb })
                    : midaLlegible(fitxer.size)
                }
              />
            </div>
          </div>
        </div>
      ) : null}
      {formatInvalid ? <p className="error-text">{t('documents.formatInvalid', { ext })}</p> : null}
      {massaGran ? (
        <p className="error-text">{t('documents.massaGran', { mida: p?.midaMaxMb })}</p>
      ) : null}
    </div>
  );

  return (
    <Modal
      screen="W-34"
      ample
      icona={<Icona nom="arrow-up" className="c-blue icon-20" />}
      titol={document ? t('documents.substitueix', { titol: document.titol }) : t('documents.puja')}
      onTanca={onTanca}
      peu={
        resultat ? (
          <>
            {!document ? (
              <Link
                className="btn btn--outline btn--s"
                to={`/admin/documentacio/${resultat.id}`}
                onClick={onTanca}
              >
                <Icona nom="doc-text" />
                {t('sistema.documents.obreDocument')}
              </Link>
            ) : null}
            <button type="button" className="btn btn--dark btn--s" onClick={onTanca}>
              {t('documents.tanca')}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn--s" onClick={onTanca}>
              {t('comu.cancella')}
            </button>
            <button
              type="button"
              className="btn btn--blue btn--s"
              disabled={!valid || puja.isPending}
              onClick={envia}
            >
              <Icona nom="arrow-up" />
              {puja.isPending ? t('documents.pujant') : t('documents.enviaAnalisi')}
            </button>
          </>
        )
      }
    >
      {resultat ? (
        <div className="info-banda" role="status">
          <Icona nom="shield-check" />
          <div>
            <strong>{t(`documents.escaneig.${versio?.estatEscaneig ?? 'PENDENT'}`)}</strong>
            <p style={{ margin: '4px 0 0' }}>
              {t('sistema.documents.resultat', { titol: resultat.titol })}
            </p>
          </div>
        </div>
      ) : document ? (
        <div className="form">
          <div className="sis-nota-gris">
            {t('sistema.documents.novaVersioDe', {
              versio: document.versioVigent
                ? t('documents.vN', { n: document.versioVigent.versio })
                : '—',
            })}
          </div>
          {zonaFitxer}
          <Camp etiqueta={t('documents.comentari')}>
            <textarea
              className="input"
              value={comentari}
              placeholder={t('sistema.documents.comentariPlaceholder')}
              onChange={(e) => setComentari(e.target.value)}
            />
          </Camp>
          <p className="camp__ajuda" style={{ margin: 0 }}>
            <Icona nom="shield" /> {t('documents.ajudaAnalisi')}
          </p>
          {puja.isError ? <p className="error-text">{missatgeError(puja.error)}</p> : null}
        </div>
      ) : (
        <div className="sis-puja">
          <section className="sis-puja__pas" aria-labelledby="puja-pas1">
            <h4 id="puja-pas1" className="sis-puja__titol">
              <span className="sis-puja__num">1</span>
              {t('sistema.documents.perQui')}
            </h4>
            <Camp etiqueta={t('documents.servei')}>
              <Desplegable
                aria-label={t('documents.servei')}
                valor={servei}
                placeholder={t('sistema.documents.triaServei')}
                opcions={opcionsServei(estructura, { seleccionat: servei === '' ? null : servei })}
                onCanvia={setServei}
              />
            </Camp>
            <div className="camp">
              <span className="camp__label">{t('sistema.documents.quiConsulta')}</span>
              <div
                className="sis-opcions-ambit"
                role="radiogroup"
                aria-label={t('documents.ambit')}
              >
                {AMBITS.map((a) => (
                  <label key={a} className="sis-opcio-ambit">
                    <input
                      type="radio"
                      name="ambit-document"
                      checked={ambit === a}
                      onChange={() => setAmbit(a)}
                    />
                    <span>
                      <strong>{t(`sistema.documents.ambit.${a}`)}</strong>
                      <span className="dim">{nomAmbit(a) ?? '—'}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </section>
          <section className="sis-puja__pas" aria-labelledby="puja-pas2">
            <h4 id="puja-pas2" className="sis-puja__titol">
              <span className="sis-puja__num">2</span>
              {t('sistema.documents.fitxerIDades')}
            </h4>
            {zonaFitxer}
            <div className="grid grid-2" style={{ gap: 12 }}>
              <Camp etiqueta={t('documents.titol')}>
                <input
                  className="input"
                  value={titol}
                  placeholder={fitxer?.name ?? ''}
                  onChange={(e) => setTitol(e.target.value)}
                />
              </Camp>
              <Camp etiqueta={t('documents.categoria')}>
                <input
                  className="input"
                  value={categoria}
                  placeholder={t('sistema.documents.categoriaPlaceholder')}
                  onChange={(e) => setCategoria(e.target.value)}
                />
              </Camp>
            </div>
            <p className="camp__ajuda" style={{ margin: '-6px 0 0' }}>
              {t('sistema.documents.ajudaTitol')}
            </p>
            <Camp etiqueta={t('documents.comentari')}>
              <textarea
                className="input"
                rows={2}
                value={comentari}
                placeholder={t('sistema.documents.comentariPlaceholder')}
                onChange={(e) => setComentari(e.target.value)}
              />
            </Camp>
            <p className="camp__ajuda" style={{ margin: 0 }}>
              <Icona nom="shield" /> {t('documents.ajudaAnalisi')}
            </p>
            {puja.isError ? <p className="error-text">{missatgeError(puja.error)}</p> : null}
          </section>
        </div>
      )}
    </Modal>
  );
}
